/**
 * Leaving a table, and removing somebody from one.
 *
 * `join` had no counterpart anywhere: nothing deleted a seat, and `reroll`
 * needs a *dead* character — so a mis-clicked "Take a seat" was permanent
 * short of deleting the campaign. Joining is deliberately ungated, which is
 * what makes it reachable by mistake in the first place.
 *
 * **Both are irreversible, so both state what they cost rather than asking
 * "are you sure?"** — the same rule the reroll confirmation follows. A seat
 * retaken later starts a fresh participation window, so the history a
 * returning player can read begins at their new arrival and not before.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, api } from '../api.js';

export function LeaveTable({ campaignId }: { readonly campaignId: string }) {
  const navigate = useNavigate();
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leave = () => {
    setBusy(true);
    setError(null);
    void api
      .delete(`/api/campaigns/${campaignId}/membership`)
      .then(() => navigate('/'))
      // The server's own words. It knows which of the three refusals applies
      // — last seat, mid-fight — and each of them names the way
      // out, which a paraphrase here would lose.
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : 'the seat could not be given up');
        setBusy(false);
      });
  };

  if (!asked) {
    return (
      <button
        type="button"
        onClick={() => setAsked(true)}
        className="text-xs text-neutral-500 dark:text-neutral-400 underline underline-offset-2"
      >
        Leave this table
      </button>
    );
  }

  return (
    <div className="space-y-1 rounded-md border border-neutral-300 p-2 text-xs dark:border-neutral-700">
      <p>
        Your seat ends. What the table has already said stays in the transcript, and you can take a
        seat again later — but you would read the story from that point on, not from where you
        left.
      </p>
      {error && (
        <p role="alert" className="text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={leave}
          className="rounded-md border border-red-600 px-2 py-0.5 disabled:opacity-40"
        >
          {busy ? 'Leaving…' : 'Leave'}
        </button>
        <button
          type="button"
          onClick={() => {
            setAsked(false);
            setError(null);
          }}
          className="rounded-md border border-neutral-300 px-2 py-0.5 dark:border-neutral-700"
        >
          Stay
        </button>
      </div>
    </div>
  );
}

export function RemoveSeat({
  campaignId,
  characterId,
  name,
  onRemoved,
}: {
  readonly campaignId: string;
  readonly characterId: string;
  readonly name: string;
  readonly onRemoved: () => void;
}) {
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!asked) {
    return (
      <button
        type="button"
        onClick={() => setAsked(true)}
        aria-label={`Remove ${name} from the table`}
        className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
      >
        Remove
      </button>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-1 text-xs">
      <span>{name} loses their seat.</span>
      {error && <span className="text-red-700 dark:text-red-400">{error}</span>}
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          void api
            .delete(`/api/campaigns/${campaignId}/members/${characterId}`)
            .then(onRemoved)
            .catch((e: unknown) =>
              setError(e instanceof ApiError ? e.message : 'the seat could not be removed'),
            )
            .finally(() => setBusy(false));
        }}
        className="rounded-md border border-red-600 px-2 py-0.5 disabled:opacity-40"
      >
        {busy ? '…' : 'Confirm'}
      </button>
      <button
        type="button"
        onClick={() => {
          setAsked(false);
          setError(null);
        }}
        className="rounded-md border border-neutral-300 px-2 py-0.5 dark:border-neutral-700"
      >
        Cancel
      </button>
    </span>
  );
}
